/**
 * How a probability is stated on the modeling board, and how two of them are compared **so that the
 * comparison agrees with what is on screen**.
 *
 * <p>The board states a probability as a percentage to two decimal places and picks out the likelier
 * side in green. Comparing the raw doubles instead paints one side green on a tie the reader sees as
 * level — two cells both reading `50.00%`, one of them green — because the two values differ far below
 * the second decimal. A four-way market makes that the normal case rather than an edge one: four
 * exhaustive endings at 25% each are a tie at every prematch, and floating point always leaves one of
 * them a hair the largest.
 */

/** A probability as every block on the board states it: a percentage to two decimal places. */
export const shownPercent = (probability: number): string => (probability * 100).toFixed(2);

/**
 * The same number, as a number, for comparisons that must agree with the screen. Read back through
 * {@link Number} rather than compared as text: `"9.09"` sorts above `"50.00"`.
 */
export const shownValue = (probability: number): number => Number(shownPercent(probability));

/**
 * Whether the first of a pair is the one to pick out: strictly ahead of the other as the board states
 * it. A pair the board shows as level picks out neither side.
 */
export const leads = (probability: number, against: number): boolean =>
  shownValue(probability) > shownValue(against);

/**
 * The value to pick out among several, or null when the board shows no clear one — two or more of them
 * stating the same number. Compare a candidate with {@link shownValue} against the result.
 */
export function clearBest(probabilities: number[]): number | null {
  if (probabilities.length === 0) {
    return null;
  }
  const shown = probabilities.map(shownValue);
  const best = Math.max(...shown);
  return shown.filter(value => value === best).length === 1 ? best : null;
}
